/**
 * Model (Contextual Model) - The semantic authority within a Bounded Context.
 *
 * A Model defines the meaning through layers. The semantic layer provides
 * the vocabulary (ontologies), while the constraint layer adds context-specific
 * rules and restrictions.
 *
 * Core invariant: Models define layers; layers do not define models.
 */

import type { SemanticLayer, ConstraintLayer } from "./SemanticLayer.ts";
import type OntologySchema from "@semantical/modules/ontology-model/OntologySchema.ts";

/**
 * Reference to an ontology in the semantic layer configuration.
 */
export interface OntologyRef {
    readonly iri: string;
    readonly label: string;
}

export interface Model {
    /** Unique identifier for the model */
    readonly id: string;

    /** Display name for the model */
    readonly name: string;

    /**
     * The semantic layer - defines vocabulary and meaning.
     * Contains ontology schemas that define the terminology.
     */
    getSemanticLayer(): SemanticLayer;

    /**
     * The constraint layer - context-specific rules.
     * Contains SHACL shapes, cardinality rules, etc.
     * Optional as constraints can be implicit.
     */
    getConstraintLayer?(): ConstraintLayer | undefined;

    /**
     * Get the primary ontology schema (main vocabulary for this context).
     */
    getPrimaryOntology(): OntologySchema | undefined;

    /**
     * Get all ontology schemas in the semantic layer.
     */
    getOntologies(): OntologySchema[];

    /**
     * Get imported ontology references (from domain or external sources).
     * These are configuration references, not loaded schemas.
     */
    getImportedOntologies(): OntologyRef[];

    /**
     * Get local ontology references (defined within this bounded context).
     * These are configuration references, not loaded schemas.
     */
    getLocalOntologies(): OntologyRef[];
}

/**
 * Default implementation of Model for runtime use.
 */
export class ModelImpl implements Model {
    readonly id: string;
    readonly name: string;

    private _semanticLayer: SemanticLayer;
    private _constraintLayer?: ConstraintLayer;
    private _importedOntologies: OntologyRef[] = [];
    private _localOntologies: OntologyRef[] = [];

    constructor(params: {
        id: string;
        name: string;
        primaryNamespace: string;
        importedOntologies?: OntologyRef[];
        localOntologies?: OntologyRef[];
    }) {
        this.id = params.id;
        this.name = params.name;
        this._semanticLayer = {
            ontologySchemas: new Map(),
            primaryNamespace: params.primaryNamespace,
            allowedNamespaces: [params.primaryNamespace]
        };
        this._importedOntologies = params.importedOntologies ?? [];
        this._localOntologies = params.localOntologies ?? [];
    }

    getSemanticLayer(): SemanticLayer {
        return this._semanticLayer;
    }

    getConstraintLayer(): ConstraintLayer | undefined {
        return this._constraintLayer;
    }

    getPrimaryOntology(): OntologySchema | undefined {
        // Return the ontology matching the primary namespace
        for (const [, schema] of this._semanticLayer.ontologySchemas) {
            if (schema.getNamespace() === this._semanticLayer.primaryNamespace) {
                return schema;
            }
        }
        // Fallback: return first ontology
        return this._semanticLayer.ontologySchemas.values().next().value;
    }

    getOntologies(): OntologySchema[] {
        return Array.from(this._semanticLayer.ontologySchemas.values());
    }

    getImportedOntologies(): OntologyRef[] {
        return this._importedOntologies;
    }

    getLocalOntologies(): OntologyRef[] {
        return this._localOntologies;
    }

    /**
     * Add an ontology schema to the semantic layer.
     */
    addOntology(schema: OntologySchema): void {
        const ns = schema.getNamespace();
        this._semanticLayer.ontologySchemas.set(ns, schema);
        if (!this._semanticLayer.allowedNamespaces.includes(ns)) {
            this._semanticLayer.allowedNamespaces.push(ns);
        }
    }

    /**
     * Set the primary namespace for the model.
     */
    setPrimaryNamespace(namespace: string): void {
        this._semanticLayer.primaryNamespace = namespace;
        if (!this._semanticLayer.allowedNamespaces.includes(namespace)) {
            this._semanticLayer.allowedNamespaces.push(namespace);
        }
    }

    /**
     * Clear all ontologies from the semantic layer.
     */
    clear(): void {
        this._semanticLayer.ontologySchemas.clear();
        this._semanticLayer.allowedNamespaces = [this._semanticLayer.primaryNamespace];
    }

    /**
     * Serialize to JSON-LD with per-object @context.
     */
    toJSON(): {
        '@context': Record<string, unknown>;
        '@type': 'Model';
        '@id': string;
        name: string;
        hasSemanticLayer: {
            '@context': Record<string, unknown>;
            '@type': 'SemanticLayer';
            includesOntology: Array<{
                '@type': 'Ontology';
                '@id': string;
                label: string;
                source: 'imported' | 'local';
            }>;
        };
    } {
        // Merge imported and local ontologies with source property
        const allOntologies = [
            ...this._importedOntologies.map(ont => ({
                '@type': 'Ontology' as const,
                '@id': ont.iri,
                label: ont.label,
                source: 'imported' as const
            })),
            ...this._localOntologies.map(ont => ({
                '@type': 'Ontology' as const,
                '@id': ont.iri,
                label: ont.label,
                source: 'local' as const
            }))
        ];

        return {
            '@context': {
                '@vocab': 'http://semantical.io/ontology/core#'
            },
            '@type': 'Model',
            '@id': `urn:semantical:model:${this.id}`,
            name: this.name,
            hasSemanticLayer: {
                '@context': {
                    '@vocab': 'http://semantical.io/ontology/core#',
                    includesOntology: { '@type': '@id', '@container': '@set' }
                },
                '@type': 'SemanticLayer',
                includesOntology: allOntologies
            }
        };
    }
}
